import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import type { DeskPayload } from '@/lib/desk/types';

/**
 * Server routes here authenticate with the caller's own Supabase JWT — a
 * browser `fetch` sends no session on its own, and `requireUser` rejects a
 * request with no `Authorization` header outright. Forgetting this produced a
 * bare "Unauthorized" toast with nothing on the page to explain it.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * The people on this workspace's Content Desk.
 *
 * ONE DESK PER WORKSPACE, PEOPLE ON IT — not one review per batch with a
 * reviewer stapled to each. A desk member's row carries `request_id IS NULL`,
 * which is what lets one link cover every batch ever shared with them.
 *
 * The LIST is a plain authenticated read under RLS. The WRITES go through
 * server routes, because both of them touch Supabase Vault (to store and to
 * re-read the link token) and `public.create_secret` raises unless the caller
 * is `service_role`.
 */

export interface DeskMember {
  id: string;
  name: string;
  email: string | null;
  createdAt: string;
  lastOpenedAt: string | null;
  openCount: number;
  revokedAt: string | null;
  /** False for a legacy packet reviewer, whose token predates the vault. */
  canCopyLink: boolean;
  /**
   * True when `approval_reviewers.passcode_hash` is set for this member. The
   * plaintext never comes down this query — nor from anywhere else, once set
   * it is only ever compared against, never re-shown — so this is a presence
   * flag, not the value.
   */
  hasPasscode: boolean;
}

export const deskMembersKey = (workspaceId: string | null) =>
  ['desk-members', workspaceId ?? ''] as const;

export function useDeskMembers(workspaceId: string | null) {
  return useQuery<DeskMember[]>({
    queryKey: deskMembersKey(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('approval_reviewers')
        .select(
          'id, name, email, created_at, last_opened_at, open_count, revoked_at, token_vault_id, request_id, passcode_hash',
        )
        .eq('workspace_id', workspaceId as string)
        .is('request_id', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id as string,
        name: row.name as string,
        email: (row.email as string | null) ?? null,
        createdAt: row.created_at as string,
        lastOpenedAt: (row.last_opened_at as string | null) ?? null,
        openCount: (row.open_count as number) ?? 0,
        revokedAt: (row.revoked_at as string | null) ?? null,
        canCopyLink: Boolean(row.token_vault_id),
        hasPasscode: Boolean(row.passcode_hash),
      }));
    },
  });
}

export function useAddDeskMember(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; email: string; passcode?: string | null }) => {
      const response = await fetch('/api/desk/members', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ ...input, workspaceId }),
      });
      const body = (await response.json()) as { token?: string; error?: string };
      if (!response.ok) throw new Error(body.error ?? 'Could not add that person.');
      return body.token as string;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deskMembersKey(workspaceId) });
    },
  });
}

/**
 * Reads an EXISTING link rather than minting a new one. The distinction is the
 * whole point: reissuing revokes the link already in the client's inbox, and
 * for two days that was the only thing an operator could do.
 */
/**
 * The operator's read of their OWN workspace desk — the same payload the client
 * gets, so the two views cannot drift. See `_load-desk.ts` for why that is one
 * loader rather than two.
 */
export const workspaceDeskKey = (workspaceId: string | null) =>
  ['desk-workspace', workspaceId ?? ''] as const;

export function useWorkspaceDesk(workspaceId: string | null) {
  return useQuery<DeskPayload>({
    queryKey: workspaceDeskKey(workspaceId),
    enabled: Boolean(workspaceId),
    /*
     * 🔴 NEVER PERSIST THIS TO THE 24-HOUR IDB CACHE — `DeskPayload` is under
     * active construction and gains fields week over week (`competitors`,
     * `voice`, `eventsTruncated`, …). A restored cache entry paints BEFORE any
     * request goes out, so a copy cached before one of those fields shipped
     * comes back missing it. `DeskRail`/`DeskView` read those fields with no
     * optional chaining — `competitors.length` on a `competitors: undefined`
     * object throws in render, which the route's error boundary then shows as
     * a bare "An error occurred" with no toast and no console signal beyond
     * the stack trace. See `useAiSessionMessages.ts` for the same fix on a
     * different payload.
     */
    meta: { persist: false },
    queryFn: async () => {
      const response = await fetch(`/api/desk/workspace/${workspaceId}`, {
        headers: await authHeaders(),
      });
      const body = (await response.json()) as DeskPayload & { error?: string };
      if (!response.ok) throw new Error(body.error ?? 'Could not open this desk.');
      return body;
    },
  });
}

export function useRevealDeskLink() {
  return useMutation({
    mutationFn: async (memberId: string) => {
      const response = await fetch(`/api/desk/members/${memberId}/link`, {
        headers: await authHeaders(),
      });
      const body = (await response.json()) as { token?: string; error?: string };
      if (!response.ok) throw new Error(body.error ?? 'Could not read that link.');
      return body.token as string;
    },
  });
}

/**
 * Turns one person's desk link off. The member row survives — who could see
 * this room, and until when, is the audit trail, not litter.
 *
 * Invalidates the member list rather than the desk payload: the operator's
 * list is what shows the revoked state, and a revoked member's link stops
 * resolving server-side regardless of any cache here.
 */
/**
 * Emails one desk member their link, on the operator's behalf.
 *
 * Operator-triggered only — there is no cron and no automatic send. This mails
 * a real person at a real client, and the moment is the operator's to choose.
 */
export function useNotifyDeskMember(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { memberId: string; isFirstSend?: boolean }) => {
      const response = await fetch(`/api/desk/members/${input.memberId}/notify`, {
        method: 'POST',
        headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // The origin the link must point at. Taken from the browser rather
          // than an env var so a link sent from localhost is obviously a
          // localhost link, instead of silently pointing at production.
          origin: window.location.origin,
          isFirstSend: input.isFirstSend ?? true,
        }),
      });
      const body = (await response.json()) as {
        sentTo?: string;
        pendingCount?: number;
        error?: string;
      };
      if (!response.ok) throw new Error(body.error ?? 'Could not send that email.');
      return body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deskMembersKey(workspaceId) });
    },
  });
}

export function useRevokeDeskMember(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const response = await fetch(`/api/desk/members/${memberId}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      const body = (await response.json()) as { revokedAt?: string; error?: string };
      if (!response.ok) throw new Error(body.error ?? 'Could not turn that link off.');
      return body.revokedAt as string;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deskMembersKey(workspaceId) });
    },
  });
}
